import { Draggable } from "@hello-pangea/dnd"
import { usePage } from "@inertiajs/react"
import {
  Archive,
  Check,
  Clock,
  GripVertical,
  RotateCcw,
  Trash2,
} from "lucide-react"
import type React from "react"

import { Button } from "@/components/ui/button"
import { useSwipeGesture } from "@/hooks/use-swipe-gesture"
import { useTaskActions } from "@/hooks/use-task-actions"
import {
  getExpiryAriaLabel,
  getTimeRemaining,
  getUrgencyLevel,
} from "@/lib/urgency"
import { cn } from "@/lib/utils"
import type { SharedData, Task } from "@/types"

interface TaskItemProps {
  task: Task
  listId: number
  enableDragDrop?: boolean
  onTaskClick?: (task: Task) => void
  onTaskDelete?: (task: Task) => void
  autoArchiveDays?: number
  dragIndex?: number
  isSelected?: boolean
}

export default function TaskItem({
  task,
  listId,
  enableDragDrop,
  onTaskClick,
  onTaskDelete,
  autoArchiveDays = 7,
  dragIndex,
  isSelected = false,
}: TaskItemProps) {
  const { today } = usePage<SharedData>().props
  const urgencyLevel = getUrgencyLevel(task, autoArchiveDays)
  const expiryAriaLabel = getExpiryAriaLabel(task, today)
  const isDyingToday = task.status === "active" && task.death_date === today

  // Check if right swipe should be enabled (for archive only on expired tasks)
  const canSwipeRight = task.status === "active" && urgencyLevel === 3

  const { handleComplete, handleArchive, handleUnarchive, handleDelete } =
    useTaskActions({
      listId,
      task,
      onDelete: onTaskDelete,
    })

  const { swipeState, swipeHandlers, fallbackTouchHandlers, getSwipeStyle } =
    useSwipeGesture({
      enabled: task.status === "active",
      canSwipeLeft: task.status === "active",
      canSwipeRight,
      onSwipeLeft: handleDelete,
      onSwipeRight: canSwipeRight ? handleArchive : undefined,
    })

  const {
    offset: swipeOffset,
    direction: swipeDirection,
    showActions,
  } = swipeState

  const renderTaskContent = (dragProvided?: {
    dragHandleProps?: React.HTMLAttributes<HTMLElement> | null
  }) => {
    return (
      <div className="relative overflow-hidden">
        {/* Left swipe action background (delete) */}
        {swipeDirection === "left" && task.status === "active" && (
          <div
            className="absolute inset-y-0 right-0 flex items-center justify-center bg-red-500/90 px-6"
            style={{ width: Math.max(Math.abs(swipeOffset), 80) }}
          >
            <Trash2
              className="h-5 w-5 text-white"
              style={{ minWidth: "20px" }}
            />
          </div>
        )}

        {/* Right swipe action background (archive for expired tasks) */}
        {swipeDirection === "right" && canSwipeRight && (
          <div
            className="absolute inset-y-0 left-0 flex items-center justify-center bg-stone-400/90 px-6"
            style={{ width: Math.max(swipeOffset, 80) }}
          >
            <Archive
              className="h-5 w-5 text-white"
              style={{ minWidth: "20px" }}
            />
          </div>
        )}

        {/* Action buttons for completed swipes */}
        {showActions &&
          swipeDirection === "left" &&
          task.status === "active" && (
            <div className="absolute inset-y-0 right-0 flex items-center bg-red-500/90 px-6">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDelete}
                className="flex items-center gap-2 text-white hover:text-white"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          )}

        {showActions && swipeDirection === "right" && canSwipeRight && (
          <div className="absolute inset-y-0 left-0 flex items-center bg-stone-400/90 px-6">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleArchive}
              className="flex items-center gap-2 text-white hover:text-white"
            >
              <Archive className="h-4 w-4" />
              Archive
            </Button>
          </div>
        )}

        {/* Task item with urgency indicator */}
        <div
          data-testid={`task-${task.id}`}
          role="option"
          aria-selected={isSelected}
          aria-label={
            expiryAriaLabel
              ? `${task.description}. ${expiryAriaLabel}`
              : task.description
          }
          className={cn(
            "group relative flex items-center gap-4 rounded-lg border px-4 py-3",
            "transition-all duration-200 ease-out",
            // Keyboard selection highlight
            isSelected &&
              "ring-red-bright bg-red-dim/30 border-red-bright/50 ring-2",
            // Base styling for active tasks
            task.status === "active" &&
              "border-border bg-bg-card hover:border-bone-dim",
            // Dying today - heartbeat animation
            isDyingToday &&
              "animate-dying-heartbeat border-l-red-bright from-red-dim to-bg-card border-l-[3px] bg-gradient-to-r",
            // Critical urgency (less than 24h but not today)
            task.status === "active" &&
              !isDyingToday &&
              urgencyLevel === 2 &&
              "border-l-red-bright from-red-dim to-bg-card border-l-[3px] bg-gradient-to-r",
            // Warning urgency (1-3 days)
            task.status === "active" &&
              urgencyLevel === 1 &&
              "border-l-yellow from-yellow-dim to-bg-card border-l-[3px] bg-gradient-to-r",
            // Expired tasks
            task.status === "active" &&
              urgencyLevel === 3 &&
              "border-l-bone-dim to-bg-card border-l-[3px] bg-gradient-to-r from-[rgba(74,72,68,0.15)] opacity-70",
            // Completed (survived) styling
            task.status === "completed" &&
              "border-border bg-transparent opacity-60 hover:opacity-80",
            // Archived (graveyard) styling
            task.status === "archived" &&
              "border-border bg-transparent opacity-50 hover:opacity-70",
            // Hover states
            onTaskClick && "cursor-pointer",
          )}
          style={getSwipeStyle()}
          onClick={(e) => {
            const target = e.target as HTMLElement
            if (
              !target.closest("button") &&
              !target.closest('input[type="checkbox"]') &&
              !target.closest('[role="checkbox"]') &&
              onTaskClick
            ) {
              onTaskClick(task)
            }
          }}
          {...(task.status === "active" ? swipeHandlers : {})}
          onTouchStart={fallbackTouchHandlers.onTouchStart}
          onTouchMove={fallbackTouchHandlers.onTouchMove}
          onTouchEnd={fallbackTouchHandlers.onTouchEnd}
        >
          {/* Drag handle */}
          {enableDragDrop && task.status === "active" && (
            <div
              {...(dragProvided?.dragHandleProps ?? {})}
              className="text-muted-foreground/40 hover:text-muted-foreground focus:ring-ring cursor-grab rounded p-0.5 transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none active:cursor-grabbing"
              aria-label="Drag to reorder task"
            >
              <GripVertical className="h-4 w-4" />
            </div>
          )}

          {/* Checkbox */}
          {(task.status === "active" || task.status === "completed") && (
            <button
              type="button"
              role="checkbox"
              aria-checked={task.status === "completed"}
              onClick={(e) => {
                if (e.altKey && task.status === "active") {
                  e.stopPropagation()
                  handleDelete()
                } else {
                  handleComplete()
                }
              }}
              className={cn(
                "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-2 transition-all",
                task.status === "active" &&
                  "border-bone-dim bg-bg-input hover:border-bone-muted",
                task.status === "completed" &&
                  "border-green bg-green hover:bg-green-bright",
                urgencyLevel === 3 && task.status === "active" && "opacity-60",
              )}
              aria-label={
                task.status === "active"
                  ? `Mark "${task.description}" as complete`
                  : `Mark "${task.description}" as incomplete`
              }
              title={
                task.status === "active"
                  ? "Complete task (Alt+Click to delete)"
                  : "Uncomplete task"
              }
            >
              {task.status === "completed" && (
                <Check className="h-3 w-3 text-white" />
              )}
            </button>
          )}

          {/* Task content */}
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                "text-[0.95rem] leading-snug font-medium transition-all duration-300",
                // Normal state
                task.status === "active" && urgencyLevel < 3 && "text-bone",
                // Expired - gentle fade
                task.status === "active" &&
                  urgencyLevel === 3 &&
                  "text-bone-muted",
                // Completed - line-through with green
                task.status === "completed" &&
                  "text-bone decoration-green-bright line-through decoration-2",
                // Archived
                task.status === "archived" && "text-bone-muted italic",
              )}
            >
              {task.description}
            </p>

            {/* Task meta: time remaining */}
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
              {/* Time remaining for active tasks */}
              {task.status === "active" &&
                task.death_date &&
                (() => {
                  const timeInfo = getTimeRemaining(task.death_date, today)
                  return (
                    <span
                      className={cn(
                        "flex items-center gap-1 font-mono font-medium tracking-wide",
                        timeInfo.urgencyLevel === "critical" &&
                          "text-red-bright",
                        timeInfo.urgencyLevel === "warning" && "text-yellow",
                        timeInfo.urgencyLevel === "safe" && "text-bone-muted",
                      )}
                    >
                      <Clock className="h-3 w-3" />
                      {timeInfo.text}
                    </span>
                  )
                })()}

              {/* Completed date for done tasks */}
              {task.status === "completed" && task.updated_at && (
                <span className="text-bone-muted font-mono">
                  Done{" "}
                  {new Date(task.updated_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              )}

              {/* Archived date for dead tasks */}
              {task.status === "archived" && task.updated_at && (
                <span className="text-bone-dim font-mono">
                  Died{" "}
                  {new Date(task.updated_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              )}
            </div>
          </div>

          {/* Action buttons - appear on hover */}
          <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            {/* Archive button for expired active tasks */}
            {task.status === "active" && task.expired && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  handleArchive()
                }}
                aria-label={`Archive "${task.description}"`}
                title="Archive"
                className="text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/60 h-7 w-7 p-0"
              >
                <Archive className="h-3.5 w-3.5" />
              </Button>
            )}

            {/* Archive button for completed tasks */}
            {task.status === "completed" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  handleArchive()
                }}
                aria-label={`Archive "${task.description}"`}
                title="Archive"
                className="text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/60 h-7 w-7 p-0"
              >
                <Archive className="h-3.5 w-3.5" />
              </Button>
            )}

            {/* Unarchive button for archived tasks */}
            {task.status === "archived" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  handleUnarchive()
                }}
                aria-label={`Restore "${task.description}"`}
                title="Unarchive"
                className="text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/60 h-7 w-7 p-0"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}

            {/* Delete button */}
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                handleDelete()
              }}
              aria-label={`Delete "${task.description}"`}
              title="Delete"
              className="text-muted-foreground/40 hover:text-destructive/70 hover:bg-destructive/10 h-7 w-7 p-0"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!enableDragDrop || task.status !== "active" || dragIndex === undefined) {
    return renderTaskContent()
  }

  return (
    <Draggable draggableId={task.id.toString()} index={dragIndex}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          {renderTaskContent(provided)}
        </div>
      )}
    </Draggable>
  )
}
